import { useState, useEffect, useRef } from 'react';
import { events } from 'aws-amplify/data';
import { useOktaAuth } from "@okta/okta-react";
import { usePartnerManagerAPI } from '../components/as2-onboarding-component/hooks/useClient';
import { DynamoDBItemExtended } from '../components/partner-manager/types';

/**
 * Transforms backend task event to frontend task format
 * FGIV1AI - Works for both http snapshot and appsync events
 */
const transformTask = (eventData: any): DynamoDBItemExtended | null => {
  try {
    // Only parse if it's an AppSync event with 'event' field
    if (eventData.event) {
      return typeof eventData.event === 'string' 
        ? JSON.parse(eventData.event) 
        : eventData.event;
    }
    
    // Return as-is (already a task object)
    return eventData;
  } catch (error) {
    console.error('Error parsing task event:', error, eventData);
    return null;
  }
};

interface UseTaskDetailReturn {
  task: DynamoDBItemExtended | null;
  loading: boolean;
  error: any;
  isConnected: boolean;
}

/**
 * FGIV1AI - Custom hook for fetching (first time) and streaming updates for a single task item
 * @param taskId - The task GUID (e.g., "9fc895e0-9633-4f3b-983d-a4d7fb432ae7")
 * @returns {{ task: Object|null, loading: boolean, error: Error|null, isConnected: boolean }}
 */
const useTaskDetail = (taskId: string): UseTaskDetailReturn => {
  const [task, setTask] = useState<DynamoDBItemExtended | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  
  const channelRef = useRef<any>(null);
  const subscriptionRef = useRef<any>(null);

  const { oktaAuth } = useOktaAuth();
  const partnerManagerClient = usePartnerManagerAPI(oktaAuth.getAccessToken());

  useEffect(() => {
    // Reset state when taskId changes
    setTask(null);
    setLoading(true);
    setError(null);
    setIsConnected(false);

    if (!taskId) {
      setLoading(false);
      return;
    }

    let mounted = true;

    const initializeTaskDetail = async (): Promise<void> => {
      try {
        // Step 1: Fetch snapshot from HTTP endpoint (CRITICAL)
        console.log(`Fetching task detail for: ${taskId}`);
        const snapshotResponse = await partnerManagerClient.get(`/tasks/${taskId}`);
 
        if (!mounted) return;

        // Extract task from wrapped response
        const taskData = snapshotResponse.data.task;
        if (!taskData) {
          throw new Error('Task not found');
        }

        // Transform and set task from snapshot
        const transformedTask = transformTask(taskData);
        
        if (!transformedTask) {
          console.warn('Failed to transform task from snapshot');
          setError(new Error('Invalid task data received'));
          setLoading(false);
          return;
        }

        setTask(transformedTask);
        setLoading(false);
        console.log(`Loaded task detail: ${transformedTask.id}`);

        // Step 2: Connect to AppSync Events for real-time updates (OPTIONAL)
        try {
          const channelPath = `/events/${taskId}`;
          
          console.log(`Connecting to task detail channel: ${channelPath}`);
          const channel = await events.connect(channelPath);
          
          if (!mounted) return;

          channelRef.current = channel;
          setIsConnected(true);

          subscriptionRef.current = channel.subscribe({
            next: (data: any) => {
              if (!mounted) return;

              console.log('Received task detail event:', data);
              
              // Transform the task data
              const transformedTask = transformTask(data);
              
              if (!transformedTask) {
                console.warn('Failed to transform task, skipping:', data);
                return;
              }

              setTask(transformedTask);
            },
            error: (err: any) => {
              if (!mounted) return;
              console.warn('AppSync subscription error (non-critical):', err);
              setIsConnected(false);
              // DON'T set error state - data already loaded from snapshot
            },
          });
        } catch (eventsError) {
          // AppSync connection failed, but snapshot data is already loaded
          console.warn('Failed to connect to AppSync Events (non-critical):', eventsError);
          console.log('Continuing with snapshot data only');
          // DON'T set error state
          // DON'T throw
        }

      } catch (err) {
        // HTTP snapshot failed - this IS an error
        if (!mounted) return;
        console.error(`Failed to load snapshot for ${taskId}:`, err);
        setError(err);
        setLoading(false);
      }
    };

    initializeTaskDetail();

    // Cleanup function
    return () => {
      mounted = false;
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe();
          console.log(`Unsubscribed from task detail events: ${taskId}`);
        } catch (err) {
          console.error('Error unsubscribing:', err);
        }
      }
      if (channelRef.current && typeof channelRef.current.close === 'function') {
        try {
          channelRef.current.close();
          console.log(`Closed task detail channel: ${taskId}`);
        } catch (err) {
          console.error('Error closing channel:', err);
        }
      }
    };
  }, [taskId]); // Re-run effect when taskId changes

  return { task, loading, error, isConnected };
};

export default useTaskDetail;